import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ArrowRight, RefreshCw } from 'lucide-react';
import axios from 'axios';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const [mode, setMode] = useState('email'); // 'email' | 'mobile'
  const [step, setStep] = useState(1); // 1=enter contact, 2=enter OTP
  const [contact, setContact] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [devOtpHint, setDevOtpHint] = useState('');
  const [timer, setTimer] = useState(30);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let interval = null;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  useEffect(() => {
    if (step === 2) {
      const focusTimer = setTimeout(() => {
        const firstInput = document.getElementById('otp-0');
        if (firstInput) {
          firstInput.focus();
        }
      }, 50);
      return () => clearTimeout(focusTimer);
    }
  }, [step]);

  const handleContactChange = (val) => {
    setContact(val);
    const isEmail = val.includes('@') || /[a-zA-Z]/.test(val);
    setMode(isEmail ? 'email' : 'mobile');
  };

  const handleSendOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!contact) return toast.error('Please enter your contact');

    const isEmail = contact.includes('@') || /[a-zA-Z]/.test(contact);
    if (isEmail) {
      if (!EMAIL_REGEX.test(contact.trim().toLowerCase())) {
        return toast.error('Please enter a valid email address');
      }
    } else {
      const cleanNum = contact.replace(/\D/g, '');
      if (cleanNum.length !== 10) {
        return toast.error('Please enter a valid 10-digit mobile number');
      }
    }

    setLoading(true);
    try {
      const sendContact = isEmail ? contact.trim().toLowerCase() : contact.replace(/\D/g, '').slice(0, 10);
      const res = await axios.post('/api/auth/send-otp', { contact: sendContact, type: isEmail ? 'email' : 'mobile' });
      setIsNewUser(res.data.isNewUser);
      setStep(2);
      setTimer(30);
      if (res.data.devOtp) {
        setDevOtpHint(res.data.devOtp);
        toast.success(`Development Mode: OTP is ${res.data.devOtp}`);
      } else {
        toast.success('OTP sent successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (idx, val) => {
    if (val.length > 1) {
      const digits = val.replace(/\D/g, '').slice(0, 6 - idx);
      if (digits) {
        const newOtp = [...otp];
        for (let i = 0; i < digits.length; i++) {
          if (idx + i < 6) {
            newOtp[idx + i] = digits[i];
          }
        }
        setOtp(newOtp);
        const nextFocus = Math.min(idx + digits.length, 5);
        document.getElementById(`otp-${nextFocus}`)?.focus();
      }
      return;
    }

    if (!/^\d*$/.test(val)) return;
    const newOtp = [...otp];
    newOtp[idx] = val;
    setOtp(newOtp);
    if (val && idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
    if (!val && idx > 0) document.getElementById(`otp-${idx - 1}`)?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;

    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      if (digits[i] !== undefined) {
        newOtp[i] = digits[i];
      }
    }
    setOtp(newOtp);

    const focusIndex = Math.min(digits.length, 5);
    document.getElementById(`otp-${focusIndex}`)?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length !== 6) return toast.error('Please enter the complete OTP');
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/verify-otp', { contact, type: mode, otp: otpStr });
      login(res.data.user, res.data.token);
      toast.success('Welcome to Seatifyai!');
      
      const destination = location.state?.from
        || (res.data.user?.role === 'admin'
          ? '/admin'
          : res.data.user?.role === 'cluster_admin'
            ? '/cluster-admin'
            : res.data.user?.role === 'institution_admin'
              ? '/institution'
              : '/courses');
      navigate(destination, { 
        state: { 
          course: location.state?.course, 
          program: location.state?.program 
        } 
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden px-4 py-6 md:min-h-screen md:p-4"
      style={{ background: 'var(--bg)' }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[12%] left-1/2 h-64 w-64 -translate-x-1/2 rounded-full opacity-15 blur-3xl md:top-1/4 md:left-1/4 md:h-96 md:w-96 md:translate-x-0"
          style={{ background: 'radial-gradient(circle, #4F46E5, transparent)' }}
        />
        <div
          className="absolute bottom-[10%] right-[-10%] h-56 w-56 rounded-full opacity-10 blur-3xl md:bottom-1/4 md:right-1/4 md:h-80 md:w-80"
          style={{ background: 'radial-gradient(circle, #7C3AED, transparent)' }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-5 text-center animate-fade-up md:mb-8">
          <img
            src="/logo.webp"
            alt="Seatifyai"
            className="mx-auto mb-3 h-auto w-auto md:mb-4"
            style={{ height: '96px', width: 'auto' }}
          />
          <p className="mx-auto mt-2 max-w-xs text-sm font-medium tracking-[0.02em] md:max-w-none" style={{ color: 'var(--text-muted)' }}>
            Smart Admission Portal - Secure and Simple
          </p>
        </div>

        <div
          className="rounded-[28px] p-5 animate-fade-up sm:p-6 md:rounded-2xl md:p-8"
          style={{
            background: 'var(--card)',
            border: '1px solid var(--card-border)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            animationDelay: '0.1s'
          }}
        >
          {step === 1 ? (
            <>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Poppins' }}>Sign In / Register</h2>

              <form onSubmit={handleSendOtp}>
                <div className="mb-5">
                  <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                    Mobile Number or Email Address
                  </label>
                  <input
                    type="text"
                    value={contact}
                    onChange={e => handleContactChange(e.target.value)}
                    placeholder="9876543210 or student@gmail.com"
                    className="w-full rounded-2xl px-4 py-3.5 text-sm outline-none transition-all focus:ring-4 focus:ring-blue-50"
                    style={{ background: '#fff', border: '1px solid var(--card-border)', color: 'var(--text)' }}
                    required
                  />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all"
                  style={{ background: 'var(--primary-gradient)', color: '#fff', opacity: loading ? 0.7 : 1 }}>
                  {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending...</>
                    : <><span>Send OTP</span><ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-3">
                <button
                  onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); }}
                  className="text-sm"
                  style={{ color: 'var(--primary)' }}
                >
                  &larr; Back
                </button>
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Poppins' }}>Verify OTP</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Sent to {contact}</p>
                    <button
                      type="button"
                      onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); }}
                      className="animate-fade-up text-xs font-semibold hover:underline"
                      style={{ color: 'var(--primary)', background: 'none', border: 'none', padding: 0 }}
                    >
                      (Edit)
                    </button>
                  </div>
                </div>
              </div>

              {devOtpHint && (
                <div className="mb-6 rounded-xl border border-blue-100 bg-blue-50 p-4 animate-pulse">
                  <p className="mb-1 text-xs font-bold text-blue-700">DEVELOPMENT MODE</p>
                  <p className="text-sm text-blue-900">Your OTP is: <span className="font-mono text-lg font-black">{devOtpHint}</span></p>
                </div>
              )}

              <form onSubmit={handleVerifyOtp}>
                <label className="mb-3 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Enter 6-digit OTP</label>
                <div className="mb-6 flex gap-2">
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`otp-${idx}`}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={e => handleOtpChange(idx, e.target.value)}
                      onKeyDown={e => e.key === 'Backspace' && !digit && idx > 0 && document.getElementById(`otp-${idx-1}`)?.focus()}
                      onPaste={handlePaste}
                      maxLength={1}
                      className="min-w-0 w-full rounded-xl py-3 text-center text-lg font-bold sm:rounded-2xl"
                      style={{ background: '#fff', border: `1.5px solid ${digit ? 'var(--primary)' : 'var(--card-border)'}`, color: 'var(--text)' }}
                    />
                  ))}
                </div>
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm"
                  style={{ background: 'var(--primary-gradient)', color: '#fff', opacity: loading ? 0.7 : 1 }}>
                  {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Verifying...</>
                    : <><span>Verify & Continue</span><ArrowRight size={16} /></>}
                </button>
                <button
                  type="button"
                  disabled={timer > 0 || loading}
                  onClick={handleSendOtp}
                  className="mt-3 flex w-full items-center justify-center gap-1 py-2 text-sm transition-all"
                  style={{
                    color: timer > 0 ? 'var(--text-muted)' : 'var(--primary)',
                    opacity: (timer > 0 || loading) ? 0.5 : 1,
                    cursor: (timer > 0 || loading) ? 'not-allowed' : 'pointer',
                    fontWeight: timer > 0 ? 'normal' : 'bold'
                  }}
                >
                  <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                  {timer > 0 ? `Resend OTP in ${timer}s` : 'Resend OTP'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mx-auto mt-5 max-w-[260px] text-center text-xs leading-5 md:mt-6 md:max-w-none" style={{ color: 'var(--text-muted)' }}>
          By continuing, you agree to our Terms of Service & Privacy Policy
        </p>
      </div>
    </div>
  );
}
